<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FireIcon,
  InboxIcon,
} from '@heroicons/vue/24/outline'
import { useActionItemStore, type ActionItem, type ActionPriority } from '@/stores/useActionItemStore'

const router = useRouter()
const actionStore = useActionItemStore()
const { items, isLoading } = storeToRefs(actionStore)

const tab = ref<'todo' | 'activity'>('todo')

onMounted(() => {
  actionStore.fetchActions()
  actionStore.fetchCount()
})

const PRIORITY_UI: Record<ActionPriority, { wrap: string; icon: typeof FireIcon }> = {
  URGENT: { wrap: 'bg-error/10 text-error', icon: FireIcon },
  HIGH: { wrap: 'bg-warning/15 text-warning', icon: ExclamationTriangleIcon },
  NORMAL: { wrap: 'bg-info/10 text-info', icon: CheckCircleIcon },
}

function priorityUi(p: ActionPriority) {
  return PRIORITY_UI[p] ?? PRIORITY_UI.NORMAL
}

function formatAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const d = Math.floor(h / 24)
  return `il y a ${d} j`
}

function closeDropdown() {
  const el = document.activeElement
  if (el instanceof HTMLElement) el.blur()
}

function runAction(item: ActionItem) {
  closeDropdown()
  const route = item.cta?.route
  if (!route) return
  router.push({ path: route, query: (item.cta?.params as Record<string, string>) ?? {} })
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-lg">
    <div class="flex items-center gap-4 border-b border-base-300 px-4 pt-3">
      <button
        type="button"
        class="pb-2 text-sm transition-colors"
        :class="tab === 'todo' ? 'border-b-2 border-primary font-bold text-primary' : 'text-base-content/60'"
        @click="tab = 'todo'"
      >
        À faire {{ items.length ? `(${items.length})` : '' }}
      </button>
      <button
        type="button"
        class="pb-2 text-sm transition-colors"
        :class="tab === 'activity' ? 'border-b-2 border-primary font-bold text-primary' : 'text-base-content/60'"
        @click="tab = 'activity'"
      >
        Activité
      </button>
      <RouterLink
        to="/mes-actions"
        class="ml-auto pb-2 text-xs text-base-content/50 transition-colors hover:text-primary"
        @click="closeDropdown"
      >
        Tout voir
      </RouterLink>
    </div>

    <div class="max-h-[28rem] overflow-y-auto p-2">
      <template v-if="tab === 'todo'">
        <div v-if="isLoading" class="flex justify-center py-8">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <div v-else-if="items.length === 0" class="flex flex-col items-center justify-center gap-2 py-10">
          <CheckCircleIcon class="h-10 w-10 text-success/70" />
          <p class="text-sm text-base-content/60">Rien à traiter — vous êtes à jour.</p>
        </div>

        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="item in items"
            :key="item.uuid"
            class="flex items-start gap-3 rounded-lg border border-base-300 p-3"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              :class="priorityUi(item.effectivePriority).wrap"
            >
              <component :is="priorityUi(item.effectivePriority).icon" class="h-5 w-5" />
            </span>

            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <p class="truncate text-sm font-bold">{{ item.title }}</p>
                <span
                  v-if="item.effectivePriority === 'URGENT'"
                  class="badge badge-error badge-sm shrink-0"
                >
                  Urgent
                </span>
              </div>
              <p v-if="item.description" class="mt-0.5 line-clamp-2 text-xs text-base-content/70">
                {{ item.description }}
              </p>
              <p class="mt-1 text-[11px] text-base-content/50">{{ formatAge(item.openedAt) }}</p>
            </div>

            <button
              v-if="item.cta?.route"
              type="button"
              class="btn btn-primary btn-sm shrink-0"
              @click="runAction(item)"
            >
              {{ item.cta?.label || 'Traiter' }}
            </button>
          </li>
        </ul>
      </template>

      <div v-else class="flex flex-col items-center justify-center gap-3 py-8">
        <InboxIcon class="h-10 w-10 text-base-content/30" />
        <p class="text-sm text-base-content/60">Notifications &amp; activité</p>
        <RouterLink to="/notifications" class="btn btn-outline btn-sm" @click="closeDropdown">
          Ouvrir mes notifications
        </RouterLink>
      </div>
    </div>
  </section>
</template>
